import { useState, useEffect, useContext } from 'react';
import { AuthContext, apiClient } from '../App';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ArrowLeft, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Mail, MessageSquare, Bell, Activity, BarChart3, FileText, Shield, Home } from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { useSafeBack } from '../hooks/useSafeBack';
import AdminHeader from '../components/AdminHeader';
import { parseUTC } from '../utils/datetime';

const StatusIndicator = ({ enabled, label }) => (
  <div className="flex items-center gap-2">
    {enabled ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-red-500" />
    )}
    <span className="text-sm">{label}</span>
  </div>
);

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-800',
    sent: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    processing: 'bg-blue-100 text-blue-800',
    confirmed: 'bg-green-100 text-green-800',
    released: 'bg-orange-100 text-orange-800',
    expired: 'bg-gray-100 text-gray-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    REMINDER_SENT: 'bg-blue-100 text-blue-800',
    FINAL_REMINDER_SENT: 'bg-purple-100 text-purple-800',
    COMPLETE: 'bg-green-100 text-green-800',
    WAITING: 'bg-gray-100 text-gray-800'
  };

  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
};

const AdminNotifications = () => {
  const { user: authUser, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/admin');

  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState(null);
  const [confirmations, setConfirmations] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);

  // Filters
  const [upcomingOnly, setUpcomingOnly] = useState(false);
  const [releasedOnly, setReleasedOnly] = useState(false);
  const [outboxStatus, setOutboxStatus] = useState('all');
  const [outboxEventType, setOutboxEventType] = useState('all');

  // Access check
  useEffect(() => {
    if (authUser && authUser.role !== 'admin' && authUser.role !== 'scout') {
      toast.error('Access denied');
      navigate('/dashboard');
    }
  }, [authUser, navigate]);

  const fetchSystemStatus = async () => {
    try {
      const response = await apiClient.get('/admin/notifications/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
      toast.error('Failed to load system status');
    }
  };

  const fetchConfirmations = async () => {
    try {
      const params = new URLSearchParams({
        days: '7',
        upcoming_only: upcomingOnly.toString(),
        released_only: releasedOnly.toString()
      });
      const response = await apiClient.get(`/admin/notifications/confirmations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfirmations(response.data.games || []);
    } catch (error) {
      console.error('Failed to fetch confirmations:', error);
      toast.error('Failed to load confirmation data');
    }
  };

  const fetchOutbox = async () => {
    try {
      const params = new URLSearchParams({
        hours: '24',
        limit: '100'
      });
      if (outboxStatus !== 'all') params.append('status_filter', outboxStatus);
      if (outboxEventType !== 'all') params.append('event_type', outboxEventType);

      const response = await apiClient.get(`/admin/notifications/outbox?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOutbox(response.data.entries || []);
      setEventTypes(response.data.event_types || []);
    } catch (error) {
      console.error('Failed to fetch outbox:', error);
      toast.error('Failed to load outbox data');
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchSystemStatus(), fetchConfirmations(), fetchOutbox()]);
    setLoading(false);
  };

  useEffect(() => {
    if (authUser?.role === 'admin' || authUser?.role === 'scout') {
      fetchAll();
    }
  }, [authUser]);

  useEffect(() => {
    if (authUser?.role === 'admin' || authUser?.role === 'scout') {
      fetchConfirmations();
    }
  }, [upcomingOnly, releasedOnly]);

  useEffect(() => {
    if (authUser?.role === 'admin' || authUser?.role === 'scout') {
      fetchOutbox();
    }
  }, [outboxStatus, outboxEventType]);

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return format(parseUTC(isoString), 'MMM d, h:mm a');
    } catch {
      return isoString;
    }
  };

  const formatRelativeTime = (isoString) => {
    if (!isoString) return 'Never';
    try {
      return formatDistanceToNow(parseUTC(isoString), { addSuffix: true });
    } catch {
      return isoString;
    }
  };

  if (!authUser || (authUser.role !== 'admin' && authUser.role !== 'scout')) {
    return null;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="admin-notifications">
      {/* Shared AdminHeader */}
      <AdminHeader title="NOTIFICATIONS" onRefresh={fetchAll} refreshing={loading} />

      {/* Content */}
      <div className="p-4 md:p-6">
        <div className="max-w-7xl mx-auto">

        {/* System Status */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6" data-testid="system-status-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5" />
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {systemStatus ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Feature Flags */}
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Feature Flags</p>
                  <StatusIndicator 
                    enabled={systemStatus.feature_flags?.notifications_enabled} 
                    label="Notifications" 
                  />
                  <StatusIndicator 
                    enabled={systemStatus.feature_flags?.confirmation_flow} 
                    label="Confirmation Flow" 
                  />
                  <StatusIndicator 
                    enabled={systemStatus.feature_flags?.email_enabled} 
                    label="Email Sending" 
                  />
                  <StatusIndicator 
                    enabled={systemStatus.feature_flags?.sms_enabled} 
                    label="SMS Sending" 
                  />
                </div>

                {/* Workers */}
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Workers</p>
                  <StatusIndicator 
                    enabled={systemStatus.workers?.confirmation_worker} 
                    label="Confirmation Worker" 
                  />
                  <p className="text-xs text-zinc-400 pl-6">
                    Last: {formatRelativeTime(systemStatus.workers?.confirmation_worker_last_run)}
                  </p>
                  <StatusIndicator 
                    enabled={systemStatus.workers?.notification_dispatcher} 
                    label="Dispatcher" 
                  />
                  <p className="text-xs text-zinc-400 pl-6">
                    Last: {formatRelativeTime(systemStatus.workers?.notification_dispatcher_last_run)}
                  </p>
                </div>

                {/* Outbox Stats */}
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Outbox</p>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-500" />
                    <span className="text-sm">Pending: {systemStatus.outbox_stats?.pending || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-sm">Failed (24h): {systemStatus.outbox_stats?.failed_24h || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Sent (24h): {systemStatus.outbox_stats?.sent_24h || 0}</span>
                  </div>
                </div>

                {/* Confirmation Stats */}
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wide">Confirmations (24h)</p>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Confirmed: {systemStatus.confirmation_stats?.confirmed || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-orange-500" />
                    <span className="text-sm">Released (24h): {systemStatus.confirmation_stats?.released_24h || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">Pending: {systemStatus.confirmation_stats?.pending || 0}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-zinc-500 py-4">Loading status...</div>
            )}
          </CardContent>
        </Card>

        {/* Confirmation Monitor Table */}
        <Card className="bg-zinc-900 border-zinc-800 mb-6" data-testid="confirmations-table-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Confirmation Monitor (Last 7 Days)
              </CardTitle>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="upcoming-only"
                    checked={upcomingOnly}
                    onCheckedChange={setUpcomingOnly}
                  />
                  <Label htmlFor="upcoming-only" className="text-sm text-zinc-400">Upcoming only</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="released-only"
                    checked={releasedOnly}
                    onCheckedChange={setReleasedOnly}
                  />
                  <Label htmlFor="released-only" className="text-sm text-zinc-400">Released only</Label>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Game</TableHead>
                    <TableHead className="text-zinc-400">ID</TableHead>
                    <TableHead className="text-zinc-400">Host</TableHead>
                    <TableHead className="text-zinc-400">Start</TableHead>
                    <TableHead className="text-zinc-400">Window Open</TableHead>
                    <TableHead className="text-zinc-400">Final Reminder</TableHead>
                    <TableHead className="text-zinc-400">Grace End</TableHead>
                    <TableHead className="text-zinc-400 text-center">Conf</TableHead>
                    <TableHead className="text-zinc-400 text-center">Rel</TableHead>
                    <TableHead className="text-zinc-400 text-center">Exp</TableHead>
                    <TableHead className="text-zinc-400">Phase</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {confirmations.length > 0 ? (
                    confirmations.map((game) => (
                      <TableRow key={game.game_id_full} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell className="font-medium text-white max-w-[150px] truncate">
                          {game.title}
                        </TableCell>
                        <TableCell className="text-zinc-400 font-mono text-xs">{game.game_id}</TableCell>
                        <TableCell className="text-zinc-400">{game.host}</TableCell>
                        <TableCell className="text-zinc-300 text-xs">{formatTime(game.start_time)}</TableCell>
                        <TableCell className="text-zinc-400 text-xs">{formatTime(game.window_open)}</TableCell>
                        <TableCell className="text-zinc-400 text-xs">{formatTime(game.final_reminder)}</TableCell>
                        <TableCell className="text-zinc-400 text-xs">{formatTime(game.grace_end)}</TableCell>
                        <TableCell className="text-center text-green-400">{game.confirmed}</TableCell>
                        <TableCell className="text-center text-orange-400">{game.released}</TableCell>
                        <TableCell className="text-center text-zinc-500">{game.expired}</TableCell>
                        <TableCell><StatusBadge status={game.current_phase} /></TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center text-zinc-500 py-8">
                        No games with confirmation activity
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Outbox Table */}
        <Card className="bg-zinc-900 border-zinc-800" data-testid="outbox-table-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Notification Outbox (Last 24 Hours)
              </CardTitle>
              <div className="flex items-center gap-4">
                <Select value={outboxStatus} onValueChange={setOutboxStatus}>
                  <SelectTrigger className="w-[120px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={outboxEventType} onValueChange={setOutboxEventType}>
                  <SelectTrigger className="w-[150px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Event Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="all">All Events</SelectItem>
                    {eventTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Created</TableHead>
                    <TableHead className="text-zinc-400">Scheduled</TableHead>
                    <TableHead className="text-zinc-400">Event</TableHead>
                    <TableHead className="text-zinc-400">Channel</TableHead>
                    <TableHead className="text-zinc-400">To</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400 text-center">Attempts</TableHead>
                    <TableHead className="text-zinc-400">Error</TableHead>
                    <TableHead className="text-zinc-400">Idempotency</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outbox.length > 0 ? (
                    outbox.map((entry, idx) => (
                      <TableRow key={`${entry.id}-${idx}`} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell className="text-zinc-400 text-xs">{formatTime(entry.created_at)}</TableCell>
                        <TableCell className="text-zinc-400 text-xs">{formatTime(entry.scheduled_for)}</TableCell>
                        <TableCell className="text-zinc-300 text-xs">{entry.notification_type}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            entry.channel === 'email' ? 'bg-blue-900/50 text-blue-300' : 'bg-zinc-800 text-zinc-400'
                          }`}>
                            {entry.channel}
                          </span>
                        </TableCell>
                        <TableCell className="text-zinc-400 text-xs font-mono">{entry.to}</TableCell>
                        <TableCell><StatusBadge status={entry.status} /></TableCell>
                        <TableCell className="text-center text-zinc-400">{entry.attempts}</TableCell>
                        <TableCell className="text-red-400 text-xs max-w-[150px] truncate" title={entry.last_error}>
                          {entry.last_error || '-'}
                        </TableCell>
                        <TableCell className="text-zinc-500 text-xs font-mono max-w-[100px] truncate" title={entry.idempotency_key}>
                          {entry.idempotency_key?.slice(0, 12)}...
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-zinc-500 py-8">
                        No notifications in the last 24 hours
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default AdminNotifications;
